import React from "react";

import { FiCpu, FiEye, FiFeather, FiGlobe, FiImage, FiLayers, FiSettings, FiShield, FiTrendingUp, FiUsers, FiZap, FiShoppingCart, FiActivity } from "react-icons/fi";

// 2. Ensure Lu icons are also present
import { LuSearch, LuClipboardList, LuCode, LuPercent, LuLayers, LuCircleCheck, LuRocket, LuShield, LuSettings, LuUsers, LuSmartphone, LuShieldCheck } from "react-icons/lu";
import { SiGooglecloud, SiOpenai, SiPytorch, SiTensorflow, SiDocker, SiKubernetes } from "react-icons/si";

// 1. Hero Block
export const aiHeroData = {
  backgroundImage: "/images/serviceBanners/AIBanner.webp",
  whiteImage: "/images/serviceBanners/AiW.webp",
  subTitle: "INTELLIGENCE ENGINEERED",
  title: (
    <>
      Intelligence <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Engineered</span> for the Future
    </>
  ),
  description:
    "We develop intelligent AI and ML solutions that are scalable, fast, and data driven to help businesses automate workflows, improve decision making, enhance efficiency, and achieve sustainable digital transformation and growth.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("AI Start clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("AI Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Optimized for speed and sub-second inference.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Scalable Infrastructure",
      desc: "Cloud-agnostic deployments (AWS/GCP/Azure).",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure & Reliable",
      desc: "Enterprise-grade model governance and privacy.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "MLOps Integrated",
      desc: "Clean, maintainable, and monitorable pipelines.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/python.svg",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
    {
      image: "/images/services/website/nodejs.webp",
      top: "8%",
      right: "14%",
      containerSize: 70,
      iconSize: 50,
      glowColor: "rgba(168,85,247,0.35)",
      iconGlow: "rgba(168,85,247,0.9)",
    },
  ],
};

// 2. Stats Block
export const aiStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Models Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Data Records",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Model Accuracy",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Inference",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const aiSolutionsAndProcessData = {
  buildSubtitle: "OUR CAPABILITIES",
  buildTitle: "Custom AI & ML Solutions for Every Business Need",
  buildCards: [
    {
      title: "High-Performance AI & ML Solutions",
      desc: "Build fast and scalable AI systems for better decision-making.",
      icon: FiZap,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Enterprise AI Solutions",
      desc: "Develop secure and scalable AI systems for complex business needs.",
      icon: FiShield,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce AI Solutions",
      desc: "Create intelligent systems to improve customer experience and sales insights.",
      icon: FiShoppingCart,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "On-Demand AI Solutions",
      desc: "Build real-time AI systems for dynamic business operations.",
      icon: FiActivity,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "FinTech AI Solutions",
      desc: "Develop secure and advanced AI models for financial intelligence.",
      icon: FiTrendingUp,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Custom AI & ML Solutions",
      desc: "Tailored AI systems designed around specific business goals.",
      icon: FiCpu,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR DEVELOPMENT PROCESS",
  processTitle: "AI & ML Development Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Consultation",
      description: "Understand business goals, data requirements, and AI opportunities.",
      icon: LuSearch,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define AI models, tech stack, and implementation roadmap.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "Model Design",
      description: "Create intelligent algorithms and system architecture.",
      icon: LuCode,
    },
    {
      step: "04",
      title: "Development",
      description: "Build scalable AI & ML solutions with clean architecture.",
      icon: LuLayers,
    },
    {
      step: "05",
      title: "Quality Testing",
      description: "Test accuracy, performance, and system reliability.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Deployment & Support",
      description: "Deploy AI systems and provide continuous optimization and support.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR AI & ML DEVELOPMENT SERVICES?",
  benefitsTitle: "Why Businesses Choose AI & ML Development Solutions",
  benefits: [
    "Faster AI development",
    "Smarter data decisions",
    "Modern AI integration",
    "High-speed processing",
    "Efficient ML workflow",
    "Scalable AI systems",
    "Cost-effective solutions",
  ],
  logoImage: "/images/serviceBanners/AIBanner.webp",
  whiteLogoImage: "/images/serviceBanners/AiW.webp",
};
// 4. Portfolio Block
export const aiPortfolioData = {
  subtitle: "OUR SUCCESS STORIES",
  title: "AI Powering Global Innovation",
  buttonText: "View Case Studies",
  buttonLink: "#",
  apps: [
    {
      title: "VisionScan AI",
      badge: "Computer Vision",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "An industrial-grade object detection system reducing manual QA time by 80%.",
      downloads: "Active",
      rating: "5.0",
      image: "/images/ai/computer-vision.webp",
    },
    {
      title: "HealthPredict",
      badge: "Predictive Analytics",
      badgeColor: "bg-[#06b6d4]/10 text-[#06b6d4] border-[#06b6d4]/20",
      desc: "Deep learning model forecasting patient outcomes with 94% accuracy.",
      downloads: "Beta",
      rating: "4.9",
      image: "/images/ai/predictive-analytics.webp",
    },
  ],
};

// 5. Technologies Block
export const aiTechnologiesData = {
  techTitle: "OUR EXTENSIVE TOOL STACK",
  technologies: [
    { name: "TensorFlow", icon: "RestAPI" },
    { name: "PyTorch", icon: "Code" },
    { name: "OpenAI", icon: "Provider" },
    { name: "Python", icon: "Dart" },
    { name: "Docker", icon: "Vite" },
    { name: "Kubernetes", icon: "Firebase" },
  ],
  trustTitle: "TRUSTED BY INNOVATORS WORLDWIDE",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

export const aiAboutData = [
  {
    title: "AI ML Development Services for Smarter Business Innovation",
    content: (
      <>
        Businesses are increasingly turning to intelligent systems to improve efficiency, reduce manual effort, and make faster decisions. Reliable{" "}
        <b className="text-text-primary font-semibold">ai ml development services</b> help organizations automate workflows, improve operational accuracy, and uncover insights from
        large amounts of data. Many companies struggle with repetitive processes and delayed decision-making that slow business growth. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, AI systems are designed around real business challenges, helping organizations adopt practical and
        scalable technologies that support measurable outcomes.
      </>
    ),
  },
  {
    title: "Artificial Intelligence Development Company for Better Automation",
    content: (
      <>
        Modern businesses need intelligent systems that can adapt to changing customer needs and operational demands. A trusted{" "}
        <b className="text-text-primary font-semibold">artificial intelligence development company</b> helps organizations improve productivity through automation, predictive
        insights, and smarter business workflows. Companies investing in <b className="text-text-primary font-semibold">machine learning solutions</b> often gain stronger
        efficiency because systems continuously improve through data-driven learning. Tycho Technologies focuses on building practical AI frameworks that support business goals
        while creating flexible solutions designed for long-term scalability.
      </>
    ),
  },
  {
    title: "AI Software Development Services for Smarter Decision-Making",
    content: (
      <>
        Organizations dealing with large datasets often require intelligent tools that simplify analysis and improve forecasting accuracy. Effective{" "}
        <b className="text-text-primary font-semibold">AI software development services</b> help businesses improve customer experiences, optimize operations, and identify growth
        opportunities with greater confidence. Businesses exploring intelligent digital transformation often strengthen innovation strategies through{" "}
        <a href="https://tychotechnologies.com/blockchain-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          blockchain development services
        </a>{" "}
        to support secure, scalable, and future-ready technology ecosystems.
      </>
    ),
  },
  {
    title: "Why Businesses Choose an AI Development Company in India",
    content: (
      <>
        The demand for a reliable <b className="text-text-primary font-semibold">ai development company in india</b> continues to grow as businesses seek practical ways to improve
        efficiency and digital transformation. Companies investing in <b className="text-text-primary font-semibold">machine learning services india</b> often prioritize
        automation, better forecasting, and scalable technology adoption. Businesses partnering with Tycho Technologies benefit from tailored AI frameworks designed around
        measurable business objectives and operational needs. Many organizations also strengthen digital growth by integrating{" "}
        <a href="https://tychotechnologies.com/trending-technologies-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          latest technology services
        </a>{" "}
        to stay competitive in fast-changing markets.
      </>
    ),
  },
];

// 7. FAQs
export const aiFaqs = [
  {
    q: "What do AI/ML development services include?",
    a: "AI/ML development services include building intelligent solutions such as predictive analytics, chatbots, recommendation systems, and computer vision models tailored to automate your business processes.",
  },
  {
    q: "Why choose AI/ML development in India?",
    a: "India offers access to world-class data scientists and cost-effective development models, delivering scalable solutions while adhering to global security standards.",
  },
];

// Legacy Support & PromptDemo Data
// Fixed: Using explicit strings to avoid any ReferenceErrors
export const PROMPTS = [
  {
    title: "Financial Analysis",
    prompt: "Summarize Q3 earnings report",
    response: "Revenue up 24% YoY. Net margin expanded 380bps. AI segment grew 3.1×.",
  },
  {
    title: "Anomaly Detection",
    prompt: "Detect anomalies in sensor stream",
    response: "Found 7 outliers • cluster #4 • confidence 98.2% • severity: high",
  },
  {
    title: "Content Generation",
    prompt: "Generate product copy for X-200",
    response: "Engineered for relentless performance — meet the X-200, your edge in motion.",
  },
];
